import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core";
import { CareplusAgencyService } from "src/services/careplus-agency-service";
import { Location } from "@angular/common";
import { MatLegacySnackBar as MatSnackBar } from "@angular/material/legacy-snack-bar";
import { MatLegacyDialog as MatDialog } from "@angular/material/legacy-dialog";
import { CareplusInitiateTxRequest } from "src/models/careplus-initiate-tx-request";
import { DiscardPaymentDialogComponent } from "../discard-payment-dialog/discard-payment-dialog.component";
import { Router } from "@angular/router";
import { StorageService } from "src/services/storage.service";

@Component({
  selector: "app-careplus-payment-checkout",
  templateUrl: "./careplus-payment-checkout.component.html",
  styleUrls: ["./careplus-payment-checkout.component.css"],
})
export class CareplusPaymentCheckoutComponent implements OnInit, OnChanges {
  private _careplusAgencyService = inject(CareplusAgencyService);
  private _storageService = inject(StorageService);
  private _snackBar = inject(MatSnackBar);
  private _dialog = inject(MatDialog);
  private location = inject(Location);
  private _route = inject(Router);
  isConfirmingPayment = false;
  isCalculated = false;
  totalAmount: number = 0;
  paymentMethod: string = "BANK_TRANSFER";
  bankName: string = "";
  transactionRef: string = "";
  chequeNo: string = "";
  transactionDate: Date | null = null;
  policyStartDate: Date | null = null;
  policyEndDate: Date | null = null;
  today: Date = new Date();
  isCalculatingPayment = false;
  isCancelling = false;
  customers: any[] = [];
  batchId: number | null = null;
  newCustomersFile: any;
  newCustomersEle: string = "";
  isUploading = false;

  @Input() selectedIds: number[] = [];
  @Input() shouldRecalculate: boolean = false;
  @Output() totalAmountChange = new EventEmitter<number>();
  @Output() onCancelled = new EventEmitter<void>();

  ngOnInit(): void {
    if (!this.selectedIds || this.selectedIds.length === 0) {
      this.totalAmountChange.emit(0);
    } else {
      this.loadBatch();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["selectedIds"] && !changes["selectedIds"].firstChange) {
      this.loadBatch();
    }
  }

  onPolicyStartDateChange(value: Date | null): void {
    if (!value) {
      this.policyEndDate = null;
      return;
    }

    const endDate = new Date(value);
    endDate.setFullYear(endDate.getFullYear() + 1);
    endDate.setDate(endDate.getDate() - 1);

    this.policyEndDate = endDate;
  }

  loadBatch() {
    if (!this.selectedIds || this.selectedIds.length === 0) {
      this.totalAmountChange.emit(0);
      return;
    }

    this.isCalculatingPayment = true;

    this._careplusAgencyService.getTotalAmount(this.selectedIds).subscribe({
      next: (res) => {
        if (this.isCancelling) return;

        if (res.status === "ONLINE_PAYMENT_INITIATED") {
          this._careplusAgencyService.cancelBatchPayment(res.id).subscribe({
            next: () => this.loadBatch(),
            error: () => (this.isCalculatingPayment = false),
          });
          return;
        }

        this.batchId = res.id;
        localStorage.setItem("careplus_batch_id", String(this.batchId));

        this.totalAmount = res.amount || 0;
        this.customers = res.customers || [];

        this.isCalculated = this.totalAmount > 0;
        this.isCalculatingPayment = false;
        this.totalAmountChange.emit(this.totalAmount);

        if (this.shouldRecalculate) {
          this.shouldRecalculate = false;
          this.recalculatePayment();
        }
      },
      error: () => {
        if (this.isCancelling) return;

        this.isCalculatingPayment = false;
        this.isCalculated = false;
        this.totalAmountChange.emit(0);
        this._snackBar.open("No form submission found.", "CLOSE", {
          horizontalPosition: "right",
          duration: 4000,
        });
      },
    });
  }

  calculatePayment() {
    this.loadBatch();
  }

  recalculatePayment() {
    if (!this.batchId) return;
    this.isCalculatingPayment = true;

    this._careplusAgencyService.recalculateBatchAmount(this.batchId).subscribe({
      next: (res) => {
        this.batchId = res.id;
        this.totalAmount = res.amount;
        this.customers = res.customers || [];
        this.isCalculated = this.totalAmount > 0;
        this.isCalculatingPayment = false;
        this.totalAmountChange.emit(this.totalAmount);
      },
      error: () => {
        this.isCalculatingPayment = false;
        this._snackBar.open(
          "Failed to calculate payment. Please try again.",
          "CLOSE",
          { horizontalPosition: "right", duration: 4000 },
        );
      },
    });
  }

  cancelPayment() {
    const dialogRef = this._dialog.open(DiscardPaymentDialogComponent, {
      width: "420px",
      panelClass: "custom-dialog",
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) return;
      this.isCancelling = true;

      const resetAndGoBack = () => {
        this.batchId = null;
        this.totalAmount = 0;
        this.customers = [];
        this.isCalculated = false;
        this.isCalculatingPayment = false;
        this.onCancelled.emit();
      };

      if (this.batchId) {
        this._careplusAgencyService.cancelBatchPayment(this.batchId).subscribe({
          next: () => resetAndGoBack(),
          error: () => resetAndGoBack(),
        });
      } else {
        resetAndGoBack();
      }
    });
  }

  confirmPayment() {
    if (!this.batchId) {
      this._snackBar.open(
        "No batch found. Please calculate payment first.",
        "CLOSE",
        { horizontalPosition: "right", duration: 4000 },
      );
      return;
    }

    if (!this.newCustomersFile) {
      this._snackBar.open("Please upload Payment Advice.", "CLOSE", {
        horizontalPosition: "right",
        duration: 4000,
      });
      return;
    }

    let requestBody: CareplusInitiateTxRequest;

    if (this.paymentMethod === "ONLINE") {
      requestBody = {
        paymentMode: this.paymentMethod,
        amount: this.totalAmount,
      };
    } else if (this.paymentMethod === "BANK_TRANSFER") {
      if (!this.bankName || !this.transactionRef || !this.transactionDate) {
        return;
      }
      requestBody = {
        paymentMode: this.paymentMethod,
        bankName: this.bankName,
        amount: this.totalAmount,
        bankTransferReference: this.transactionRef,
        transactionDate: this.transactionDate!,
        policyStartDate: this.policyStartDate,
        policyEndDate: this.policyEndDate
      };
    } else if (this.paymentMethod === "CHEQUE_TRANSFER") {
      if (!this.bankName || !this.chequeNo || !this.transactionDate) {
        return;
      }
      requestBody = {
        paymentMode: this.paymentMethod,
        bankName: this.bankName,
        amount: this.totalAmount,
        chequeNo: this.chequeNo,
        transactionDate: this.transactionDate!,
      };
    } else {
      this._snackBar.open("Invalid payment method selected.", "CLOSE", {
        horizontalPosition: "right",
        duration: 4000,
      });
      return;
    }
    this.isConfirmingPayment = true;

    this._careplusAgencyService
      .initiateTx(this.batchId, requestBody)
      .subscribe({
        next: (res) => {
          localStorage.removeItem("careplus_selected_ids");
          this.uploadToCloud(this.newCustomersFile);
          if (this.paymentMethod === "ONLINE" && res.paymentUrl) {
            window.location.href = res.paymentUrl;
          } else {
            this._snackBar.open("Payment initiated successfully.", "CLOSE", {
              horizontalPosition: "right",
              duration: 4000,
            });
            this.isConfirmingPayment = false;
            this._route.navigate(["/careplus/employee-payment-log"]);
          }
        },
        error: () => {
          this.isConfirmingPayment = false;
          this._snackBar.open(
            "Failed to initiate payment. Please try again.",
            "CLOSE",
            { horizontalPosition: "right", duration: 4000 },
          );
        },
      });
  }

  back() {
    this.location.back();
  }

  onnewCustomers(event: Event) {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;
    this.newCustomersEle = file.name;
    this.newCustomersFile = file;
  }

  uploadCSV() {
    if (this.newCustomersFile) {
      this.uploadToCloud(this.newCustomersFile);
    }
  }

  private uploadToCloud(file: File) {
    console.log(this.batchId);
    this.isUploading = true;

    const d = new Date().getTime().toString();
    const fileName = `${this.batchId}`;

    this._storageService
      .getCareplusUploadOnlyToken("careplus-batch-payment-attachment")
      .subscribe((token) => {
        this._storageService
          .uploadFileToAzure(
            `/careplus-batch-payment-attachment/${fileName}${token.sasToken}`,
            file,
            file.type,
          )
          .subscribe({
            next: () => {
              this.isUploading = false;
            },
            error: () => {
              this.isUploading = false;
            },
          });
      });
  }

  onStartDateChange(date: Date | null) {
    if (!date) {
      this.policyEndDate = null;
      return;
    }
    const end = new Date(date);
    end.setFullYear(end.getFullYear() + 1);
    end.setDate(end.getDate() - 1);
    this.policyEndDate = end;
  }
}
